import { useState } from 'react'
import { useNavigate } from 'react-router'

import { type RecipeV2Doc } from '@ctcells/api'
import { t } from '@ctcells/i18n'

import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { SearchInput } from '../../atoms/SearchInput.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { Toolbar } from '../../atoms/Toolbar.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { type ScreenAccess } from '../access.ts'
import { useGroupTabLayout } from '../groupTabs.ts'
import { OfflineNotice, RowLink } from '../resourceParts.tsx'
import { RESOURCE_TABS, type ResourceTabId } from '../resources/resourceTabs.ts'

import {
  recipeTemplateDetailPath,
  recipeTemplateRow,
  type RecipeTemplateRow,
} from './templateModel.ts'

import '../screens.css'
import '../resource.css'

/**
 * 작업 준비 › **템플릿** — 🔴 **「레시피」 템플릿(서버 표준 레시피 한 벌) 하나만 선다**(eric 2026-10-26 「응.. 다 빼줘」).
 *
 * ```
 * 작업 준비
 * [시료 | 레시피 | 템플릿*]                                      [검색]   ← 시료 탭과 같은 틀
 * 이름
 * CTCells 표준 시나리오 …                                                 → 행을 누르면 오른쪽 판(레시피 상세)
 * ```
 *
 * 🔴 데크 · 작업(그리고 데크 표를 통해서만 들어가던 랩웨어 · 시약) 템플릿은 걷었다 — 종류 칩 줄 · 「＋ 템플릿 추가」도 같이 걷었다.
 *    서버의 그 템플릿을 쓰는 창구(`POST /recipes/compose`)를 부르는 화면이 없었다. 서버 데이터 · API 는 그대로다.
 * 🔴 조회는 `GET /recipes/standard` 하나. 열은 응답에 있는 값만 — 이름(`metadata.protocolName`). 「타입」 열은 종류가 하나라 걷었다.
 * 🔴 레시피 템플릿은 만드는 창구가 서버에 없다(BR-129) — 추가 단추가 없다.
 * ⚠ 조회는 역할 무관(viewer 도 본다 · `recipes_v2.py` `ROLES`).
 */
export interface RecipeTemplateSource {
  doc?: RecipeV2Doc | undefined
  loading?: boolean | undefined
  error?: string | undefined
}

export interface TemplatesScreenProps {
  /** 표준 레시피 한 벌. 안 넘기면 없음(목 문서를 짓지 않는다) */
  recipe?: RecipeTemplateSource | undefined
  /** 오른쪽 판에 열린 줄의 식별자 */
  selectedId?: string | undefined
  resourceTab?: ResourceTabId | undefined
  access?: ScreenAccess
}

const DEFAULT_SORT: TableSort = { key: 'name', direction: 'asc' }

export function TemplatesScreen({
  recipe = {},
  selectedId,
  resourceTab,
  access = {},
}: TemplatesScreenProps) {
  const navigate = useNavigate()
  const groupTabs = useGroupTabLayout(RESOURCE_TABS, resourceTab, 'templates', access)
  const [query, setQuery] = useState('')

  const all = recipe.doc === undefined ? [] : [recipeTemplateRow(recipe.doc)]
  const needle = query.trim().toLowerCase()
  const rows = all.filter(
    (row) => needle === '' || [row.name, row.id].some((v) => v.toLowerCase().includes(needle)),
  )

  const columns: Column<RecipeTemplateRow>[] = [
    {
      key: 'name',
      header: t('column.name'),
      sort: 'text',
      sortValue: (row) => row.name,
      cell: (row) => <RowLink to={recipeTemplateDetailPath(row.id)}>{row.name}</RowLink>,
    },
  ]

  return (
    <ListPage
      {...(groupTabs
        ? {
            onTabChange: groupTabs.onChange,
            tabValue: groupTabs.value,
            tabs: groupTabs.tabs,
            tabsLabel: t('screen.resourcesGroup.title'),
          }
        : {})}
      title={groupTabs ? t('screen.resourcesGroup.title') : t('screen.templates.title')}
      toolbar={
        <Toolbar label={t('screen.templates.title')}>
          {/* 눈에 보이는 글자는 「검색」뿐 — 시료 · 레시피 탭과 같다(eric 2026-09-30). 찾는 칸(이름 · ID)은 접근성 이름으로 남는다 */}
          <SearchInput
            label={t('filter.search.templates')}
            onChange={setQuery}
            placeholder={t('filter.search.batchesPlaceholder')}
            value={query}
          />
        </Toolbar>
      }
      /* 시료 · 레시피 탭과 «같은 틀» — 검색은 무리 탭 줄 오른쪽 같은 줄(`toolbarInTabs`) */
      toolbarInTabs={groupTabs !== null}
    >
      <OfflineNotice shown={access.offline === true} />
      {/* 못 받았으면 사유 — 「0건」으로 접지 않는다 */}
      {recipe.error === undefined ? null : (
        <p className="deny" role="alert">
          {recipe.error}
        </p>
      )}
      <DataTable
        caption={t('screen.templates.title')}
        columns={columns}
        defaultSort={DEFAULT_SORT}
        emptyLabel={recipe.error ?? t('state.empty.templates')}
        /* 표 아래 「N건」은 세우지 않는다(eric 2026-10-13 「2건이라는 건 빼줘」) */
        hideCount
        loading={recipe.loading === true}
        onRowClick={(row) => void navigate(recipeTemplateDetailPath(row.id))}
        rowKey={(row) => row.id}
        rows={rows}
        selected={(row) => row.id === selectedId}
      />
    </ListPage>
  )
}
